import { CompetitionRequest, CompetitionSerializerSmall, PatchedCompetitionRequest } from '~/apis/ssf';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthentication } from '~/hooks/useAuthentication';
import { SSF } from '~/apis/api';
import { keys } from './queries';

function useCreateCompetitionCallback() {
  const { setUser } = useAuthentication();

  function handle(competition: CompetitionSerializerSmall) {
    setUser(
      prev =>
        prev && {
          ...prev,
          permissions: {
            ...prev.permissions,
            competitions_admin: [...prev.permissions.competitions_admin, competition],
          },
        }
    );
  }

  return {
    handle,
  };
}

function useUpdateCompetitionCallback() {
  const { setUser } = useAuthentication();

  function handle(competition: CompetitionSerializerSmall) {
    setUser(prev => {
      if (!prev) {
        return undefined;
      }

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          competitions_admin: prev.permissions.competitions_admin.map(ca => {
            if (ca.id !== competition.id) {
              return ca;
            }

            return {
              ...ca,
              name: competition.name,
            };
          }),
        },
      };
    });
  }

  return {
    handle,
  };
}

function useDeleteCompetitionCallback() {
  const { setUser } = useAuthentication();

  function handle(competitionId: number) {
    setUser(prev => {
      if (!prev) {
        return undefined;
      }

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          competitions_admin: prev.permissions.competitions_admin.filter(ca => ca.id !== competitionId),
        },
      };
    });
  }

  return {
    handle,
  };
}

export function useCreateCompetitionMutation() {
  const callback = useCreateCompetitionCallback();
  const client = useQueryClient();

  return useMutation({
    mutationFn: SSF.api.competitionsCreate,
    onSuccess: ({ data }) => {
      client.invalidateQueries({ queryKey: keys.lists() });
      callback.handle(data);
    },
  });
}

export function useUpdateCompetitionMutation() {
  const callback = useUpdateCompetitionCallback();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CompetitionRequest }) =>
      SSF.api.competitionsUpdate(id, payload),
    onSuccess: ({ data }) => {
      client.invalidateQueries({ queryKey: keys.detail(data.id) });
      client.invalidateQueries({ queryKey: keys.lists() });
      callback.handle(data);
    },
  });
}

export function usePartialUpdateCompetitionMutation() {
  const callback = useUpdateCompetitionCallback();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PatchedCompetitionRequest }) =>
      SSF.api.competitionsPartialUpdate(id, payload),
    onSuccess: ({ data }) => {
      client.invalidateQueries({ queryKey: keys.detail(data.id) });
      client.invalidateQueries({ queryKey: keys.lists() });
      callback.handle(data);
    },
  });
}

export function useDeleteCompetitionMutation() {
  const callback = useDeleteCompetitionCallback();
  const client = useQueryClient();

  return useMutation({
    mutationFn: SSF.api.competitionsDestroy,
    onSuccess: (_, competitionId) => {
      client.invalidateQueries({ queryKey: keys.lists() });
      callback.handle(competitionId);
    },
  });
}
